import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Quote, Star } from 'lucide-react';

const TestimonialsPage = () => {
  const testimonials = [
    {
      quote: "Working with Maison LouLou completely transformed how I approach getting dressed each morning. My closet finally makes sense! She understood exactly what I needed and helped me build a wardrobe that feels authentically me.",
      author: "Scott",
      location: "Pasadena, CA",
      service: "Closet Cleanse",
      rating: 5
    },
    {
      quote: "The interior design consultation exceeded all my expectations. My home now feels like a true coastal retreat. Denise has an incredible eye for detail and really listened to what I wanted.",
      author: "Jennifer K.",
      location: "Del Mar, CA",
      service: "Room Refresh",
      rating: 5
    },
    {
      quote: "I never knew I could feel this confident in my own wardrobe. The complete wardrobe overhaul was life changing.",
      author: "Danielle C.",
      location: "Seattle, WA",
      service: "Closet Cleanse",
      rating: 5
    },
    {
      quote: "As a busy professional, I didn't have time to shop or think about my wardrobe. Denise created a complete shopping list and even handled everything virtually. I couldn't be happier with the results.",
      author: "Emily C.",
      location: "San Diego, CA",
      service: "Full Shopping Spree",
      rating: 5
    },
    {
      quote: "Our living room went from cluttered and chaotic to calm and beautiful. Denise knew exactly how to maximize our space while keeping it functional for our family. She truly is talented.",
      author: "Michael & Lisa T.",
      location: "Carlsbad, CA",
      service: "Full Home Design",
      rating: 5
    },
    {
      quote: "I was nervous about spending money on a stylist, but it was the best investment I've made in myself. Denise helped me understand what colors and styles work for my body. Worth every penny!",
      author: "Rachel G.",
      location: "Pacific Beach, CA",
      service: "Closet Cleanse",
      rating: 5
    },
    {
      quote: "The before and after of my bedroom is unbelievable. It went from a cluttered mess to a serene sanctuary. I actually look forward to going to bed now—the space is that calming.",
      author: "David P.",
      location: "Coronado, CA",
      service: "Room Refresh",
      rating: 5
    },
    {
      quote: "I'd been struggling with my wardrobe for years. In just one session, Denise helped me see my clothes in a completely new way. She created outfit combinations I never would have thought of on my own.",
      author: "Stephanie B.",
      location: "Point Loma, CA",
      service: "In-Home Styling",
      rating: 5
    },
    {
      quote: "Denise styled me for my daughter's wedding and I felt absolutely beautiful. She found the perfect dress and accessories. I received so many compliments and felt confident all day.",
      author: "Margaret W.",
      location: "Rancho Santa Fe, CA",
      service: "Event Styling",
      rating: 5
    }
  ];

  const featuredTestimonial = testimonials[0];

  return (
    <div data-testid="testimonials-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
            Client Testimonials
          </p>
          <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-8 leading-tight max-w-4xl mx-auto">
            What Our Clients Say
          </h1>
          <p className="font-sans text-[#57534E] text-lg leading-relaxed max-w-2xl mx-auto">
            Don't just take our word for it. Hear from the incredible women and families 
            we've had the pleasure of working with.
          </p>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="bg-[#2C3E50] rounded-3xl p-10 md:p-16 text-white relative overflow-hidden">
            <Quote className="absolute top-8 right-8 h-24 w-24 text-white/10" />
            <div className="relative z-10">
              <div className="flex gap-1 mb-6">
                {[...Array(featuredTestimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-[#D4A373] text-[#D4A373]" />
                ))}
              </div>
              <blockquote className="font-serif text-2xl md:text-3xl leading-relaxed mb-8 italic">
                "{featuredTestimonial.quote}"
              </blockquote>
              <div>
                <p className="font-serif text-xl text-[#D4A373]">
                  {featuredTestimonial.author}
                </p>
                <p className="font-sans text-white/60 text-sm">
                  {featuredTestimonial.location} • {featuredTestimonial.service}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Testimonials */}
      <section data-testid="all-testimonials" className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(1).map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-card-${index}`}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#D4A373] text-[#D4A373]" />
                  ))}
                </div>
                <p className="font-sans text-[#57534E] leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-[#E5E7EB] pt-4">
                  <p className="font-serif text-[#2C3E50] text-lg">
                    {testimonial.author}
                  </p>
                  <p className="font-sans text-[#D4A373] text-sm">
                    {testimonial.location}
                  </p>
                  <p className="font-sans text-[#57534E] text-xs mt-1">
                    {testimonial.service}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#2C3E50] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Ready to Join Them?
          </h2>
          <p className="font-sans text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Let's create your own transformation story. 
            Book a consultation and start your journey today.
          </p>
          <Link to="/contact">
            <Button
              data-testid="testimonials-cta-btn"
              className="rounded-full bg-[#D4A373] hover:bg-[#c29260] text-white px-10 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              Book Your Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
